import { DiscountCode } from './DiscountCodeTypes'
import { EnrollmentRegisteredPartner } from './GrooverTypes'

export type DiscountAppliesTo = 'course' | 'mainUser' | 'partnerUser' | 'allParticipants'

export type DiscountScope = 'Activity' | 'Enrollment' | 'Period'

export type DiscountBase = {
  id: string
  type: string
  name: string
  value: number
  calculationType: 'percentage' | 'amount'
  appliesTo: DiscountAppliesTo
  discountScope?: DiscountScope
  canBeCombined: boolean
  // Make these optional for backward compatibility
  discountGroup?: string
  excludeGroupInCombination?: string
  courseDiscounts?: CourseMatrixDiscount[]
  isDeleted?: boolean // Soft delete flag - discounts with isDeleted: true are filtered out
}
export type DiscountNone = DiscountBase & {
  type: 'none'
  appliesTo: 'allParticipants'
}

export type DiscountCombined = DiscountBase & {
  type: 'combined'
  appliesTo: 'allParticipants'
}

export type CourseMatrixDiscount = {
  course: number // which course (the first, the second, etc) the discount applies to
  discount: number // the discount
}

/**
 * Controls how slot counting is scoped for `multi_course_matrix` discounts.
 *
 * - **Enrollment** (default): tiers apply to the Nth course in the current basket only.
 * - **Period**: tiers apply per activity period. Slot = price-rank within the period
 *   (per `strategy`) + count of prior enrollments (paid or confirmed-unpaid) in that period + 1.
 *   Each period in the basket is counted independently.
 *
 * Period mode requirements:
 * - Activities must include this discount's id in `activity.discountIds` (same as enrollment mode).
 * - Each tier matches an **exact** slot number (`course` === slot). A tier at course 2 applies
 *   only to slot 2, not to slots 3, 4, … — configure one tier per slot you want to discount.
 * - Prior enrollments come from `userEnrolledActivityPeriods` (`getUserPreviousDiscounts`), unique
 *   by activity ID (paid and non-fully-paid, status Active/Confirmed/Pending). Re-registering the
 *   same activity does not occupy a second slot. Activities already in the current basket are
 *   excluded from the prior offset so they are ranked in the basket instead.
 * - Legacy `userEnrolledPeriodIds` (one entry per enrollment-activity row) is used only when
 *   `userEnrolledActivityPeriods` is omitted.
 */
export enum DiscountMatrixContext {
  /** Default: only activities in the current basket are counted. */
  Enrollment = 'enrollment',
  /** Basket + prior enrollments (paid or confirmed-unpaid) in the same period(s), per-period tiered discounts. */
  Period = 'period'
}

/** One distinct prior activity and the period it was enrolled in. Used by period-mode matrix discounts. */
export type UserEnrolledActivityPeriod = {
  activityId: string
  periodId: string
}

export type DiscountMultiCourseMatrix = DiscountBase & {
  type: 'multi_course_matrix'
  appliesTo: 'allParticipants'
  strategy: 'max_price' | 'min_price' // | 'first' | 'last'
  courseDiscounts: CourseMatrixDiscount[]
  studentsOnly?: boolean
  /** @see DiscountMatrixContext — defaults to Enrollment when omitted. */
  matrixContext?: DiscountMatrixContext
  /**
   * Optional hard cap on the cumulative discounted total across the basket (in smallest currency
   * unit, e.g. öre for SEK). After all per-slot tier discounts are applied, a second pass walks
   * the sorted activity groups and boosts the discount on any activity that would push the running
   * total over this value. Activities that fall beyond the cap get their finalPrice reduced to 0.
   */
  maxTotalPrice?: number
}

export type DiscountCouplesRegistration = DiscountBase & {
  type: 'couples'
  appliesTo: 'allParticipants'
}

export type DiscountStudent = DiscountBase & {
  type: 'student'
  appliesTo: 'mainUser' | 'partnerUser' | 'allParticipants'
}

export type DiscountSibling = DiscountBase & {
  type: 'sibling'
  appliesTo: 'mainUser' | 'partnerUser' | 'allParticipants'
}

export type DiscountSenior = DiscountBase & {
  type: 'senior'
  appliesTo: 'mainUser'
}

export type DiscountCouponCode = DiscountBase & {
  type: 'code'
  appliesTo: 'allParticipants'
}

export type DiscountEarlyBird = DiscountBase & {
  earlyBirdDeadline: string // ISO date string, e.g. '2024-07-01'
  type: 'early_bird'
  appliesTo: 'course'
}

export type DiscountMember = DiscountBase & {
  type: 'member'
  appliesTo: 'mainUser' | 'partnerUser' | 'allParticipants'
}

export type DiscountComboDiscount = DiscountBase & {
  type: 'combo'
  appliesTo: 'course'
  coursesDiscounts: CourseComboDiscount[]
}

export type CourseComboDiscount = {
  name?: string
  parentEventId?: string // ID of the parent event (eventType === 'Parentevent')
  courses: string[] // Array of subevent IDs
  discount: number
}

export type DiscountPricingConfig = {
  id: string // Unique identifier for this pricing configuration
  seatsAvailable: number
  price: number
  registrationType: 'single' | 'couple' // Default: 'single'
  packageName?: string // Optional package name for the pricing tier (e.g., "Package 1", "Super Early Bird", etc.)
  region: string // Region this pricing applies to (e.g., 'Global', 'Nordic', 'Stockholm')
  label?: string // Optional label for the pricing tier (e.g., "Early Bird", "Regular", etc.)
}

export type DiscountAdvanced = DiscountBase & {
  type: 'advanced'
  maxRegularSeats: number
  pricingConfigs: DiscountPricingConfig[]
}

export type DiscountPriceOverride = DiscountBase & {
  type: 'price_override'
  price: number
}

/**
 * How often a continuation discount may be granted after the user has qualified.
 *
 * - **Period** (default): once the discount has been applied on any enrollment, later
 *   registrations do not get it again.
 * - **Activity**: the discount may apply once per distinct activity. A later registration
 *   for a different activity still gets it; re-registering the same activity does not.
 */
export enum ContinuationDiscountApplyLimit {
  Period = 'period',
  Activity = 'activity'
}

/** One prior application of a continuation discount, used by Activity apply-limit. */
export type UsedContinuationDiscountActivity = {
  discountId: string
  activityId: string
}

export type DiscountContinuation = DiscountBase & {
  type: 'continuation'
  appliesTo: 'allParticipants'
  /** @deprecated Prefer qualifyingPeriodIds. Still read for existing client docs. */
  qualifyingPeriodId?: string
  qualifyingPeriodIds: string[]
  qualifyingActivityIds: string[] // when non-empty, enrollment in any of these activities qualifies the user and the qualifying-period check is skipped; when empty, the user must be enrolled in one of qualifyingPeriodIds instead
  /** @see ContinuationDiscountApplyLimit — defaults to Period when omitted. */
  applyLimit?: ContinuationDiscountApplyLimit
}

export type Discount =
  | DiscountNone
  | DiscountCombined
  | DiscountMultiCourseMatrix
  | DiscountContinuation
  | DiscountCouplesRegistration
  | DiscountStudent
  | DiscountSibling
  | DiscountSenior
  | DiscountCouponCode
  | DiscountEarlyBird
  | DiscountMember
  | DiscountAdvanced
  | DiscountPriceOverride
  | DiscountComboDiscount

export type PriceDiscount = {
  originalPrice: number
  discountedPrice: number
  discountUsed: Discount
  appliesTo: DiscountAppliesTo
}

export type DiscountUserInputV1 = {
  coursesAmount: number
  participantsAmount: number
  isStudent?: boolean
  isSenior?: boolean
  discountCode?: DiscountCode
}

export type DiscountUserInputV2 = {
  mainUser: {
    isStudent?: boolean
    isSibling?: boolean
    isSenior?: boolean
    userActivePeriods?: string[]
  }
  partnerUser?: {
    isStudent?: boolean
    isSibling?: boolean
    isSenior?: boolean
    userActivePeriods?: string[]
  }
  coursesAmount: number
  participantsAmount: number
  discountCode?: DiscountCode
}

export type PriceOverride = {
  activityId: string
  price: number
  type: 'earlyBird' | 'manual'
  reference: string // earlyBirdBookingId or manual override id
  label?: string // Optional label for the pricing tier
}

export type DiscountUserInputV3 = {
  mainUser: {
    isStudent?: boolean
    isSibling?: boolean
    isSenior?: boolean
    userActivePeriods?: string[]
    userEnrolledActivityIds?: string[]
    userEnrolledPeriodIds?: string[]
    userEnrolledActivityPeriods?: UserEnrolledActivityPeriod[]
    usedContinuationDiscountIds?: string[]
    usedContinuationDiscountActivities?: UsedContinuationDiscountActivity[]
  }
  partnerUser?: {
    isStudent?: boolean
    isSibling?: boolean
    isSenior?: boolean
    userActivePeriods?: string[]
    userEnrolledActivityIds?: string[]
    userEnrolledPeriodIds?: string[]
    userEnrolledActivityPeriods?: UserEnrolledActivityPeriod[]
    usedContinuationDiscountIds?: string[]
    usedContinuationDiscountActivities?: UsedContinuationDiscountActivity[]
  }
  coursesAmount: number
  participantsAmount: number
  registeredPartners?: EnrollmentRegisteredPartner[]
  priceOverride?: PriceOverride[]
  discountCode?: DiscountCode
}
